import React, { useRef, useState, useEffect, useMemo } from 'react';
import { generateFramePaths } from '@/utils/geometry';
import clsx from 'clsx';

interface GeometricFrameProps {
  className?: string;
  stroke?: string;
  strokeWidth?: number;
  radius?: number;

  // Horizontal Config
  hGap?: number;
  hLine?: boolean;
  hMode?: 'center' | 'edge';
  hSize?: number;
  hDashArray?: string;

  // Vertical Config
  vGap?: number;
  vLine?: boolean;
  vMode?: 'center' | 'edge';
  vSize?: number;
  vDashArray?: string;
}

/**
 * A responsive frame component that draws a geometric border (brackets/corners)
 * tailored to its container's size.
 */
export const GeometricFrame: React.FC<GeometricFrameProps> = ({
  className,
  stroke = '#BFB0F8',
  strokeWidth = 3,
  radius = 20,
  hGap = 24,
  hLine = false,
  hMode = 'center',
  hSize = 48,
  hDashArray,
  vGap = 24,
  vLine = false,
  vMode = 'center',
  vSize = 0,
  vDashArray,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  useEffect(() => {
    if (!containerRef.current) return;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setDimensions({
          width: entry.contentRect.width,
          height: entry.contentRect.height,
        });
      }
    });

    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  const paths = useMemo(() => {
    if (dimensions.width === 0 || dimensions.height === 0) return null;
    return generateFramePaths({
      width: dimensions.width,
      height: dimensions.height,
      strokeWidth,
      radius,
      hGap,
      hLine,
      hMode,
      hSize,
      vGap,
      vLine,
      vMode,
      vSize,
    });
  }, [
    dimensions.width,
    dimensions.height,
    strokeWidth,
    radius,
    hGap,
    hLine,
    hMode,
    hSize,
    vGap,
    vLine,
    vMode,
    vSize,
  ]);

  return (
    <div ref={containerRef} className={clsx('pointer-events-none absolute inset-0', className)}>
      {paths && (
        <svg
          width='100%'
          height='100%'
          viewBox={`0 0 ${dimensions.width} ${dimensions.height}`}
          className='overflow-visible'
        >
          <g fill='none' stroke={stroke} strokeWidth={strokeWidth} strokeLinecap='butt'>
            <path d={paths.topLeft} />
            <path d={paths.topRight} />
            <path d={paths.bottomRight} />
            <path d={paths.bottomLeft} />
            {paths.topCenter && <path d={paths.topCenter} strokeDasharray={hDashArray} />}
            {paths.bottomCenter && <path d={paths.bottomCenter} strokeDasharray={hDashArray} />}
            {paths.leftCenter && <path d={paths.leftCenter} strokeDasharray={vDashArray} />}
            {paths.rightCenter && <path d={paths.rightCenter} strokeDasharray={vDashArray} />}
          </g>
        </svg>
      )}
    </div>
  );
};
